import React from "react";
import { useDispatch, useSelector } from "react-redux";
import Carousel from "react-material-ui-carousel";
import _ from "lodash";

import { texts as stepOneTexts } from "../steps/stepOne";
import { stepTwoTexts } from "../steps/stepTwo";
import MobileDialog from "../../components/MobileDialog";
import { MobileSelectedTextCard } from "../../components/textCardMobile/MobileSelectedTextCard";
import useWindowDimensions from "../../hooks/useWinDims";

import { makeStyles, Card, CardContent, Typography, Link, Fab } from "@material-ui/core";
import { Add, Check } from "@material-ui/icons";
import { green, yellow, orange, red } from "@material-ui/core/colors";

const useStyles = makeStyles(
  () => ({
    root: {
      height: "calc(100% - 77px)",
      padding: "10px",
      width: "100%"
    },
    card: {
      borderRadius: "12px",
      boxShadow: "0 1.5px 12px 2px rgb(0 0 0 / 6%)",
      height: "100%"
    },
    cardContent: {
      height: "calc(100% - 40px)",
      paddingBottom: "16px !important"
    },
    cardFooter: {
      alignItems: "baseline",
      display: "inline-flex",
      justifyContent: "space-between",
      width: "100%"
    },
    cardHeading: {
      fontWeight: "bold"
    },
    carousel: {
      borderRadius: "12px",
      width: "100%"
    },
    heading: {
      display: "inline-flex",
      justifyContent: "space-between",
      marginBottom: "10px",
      width: "100%"
    },
    text: {
      display: "flex",
      userSelect: "none",
      width: "100%"
    },
    textOverflow: {
      display: "flex",
      height: "calc(100% - 55px)",
      maskImage: "linear-gradient(to bottom, black 65%, transparent 100%)",
      overflow: "hidden"
    }
  }),
  { name: "MobileStepTwo" }
);

const mapRelationToText = (relation) => {
  let text = "";
  let color = "";
  switch (relation) {
    case 0:
      text = "Sehr verwandt";
      color = green[500];
      break;
    case 1:
      text = "Verwandt";
      color = green[200];
      break;
    case 2:
      text = "Etwas verwandt";
      color = yellow[500];
      break;
    case 3:
      text = "Wenig verwandt";
      color = yellow[700];
      break;
    case 4:
      text = "Kaum verwandt";
      color = orange[200];
      break;
    default:
      text = "Nicht verwandt";
      color = red[500];
      break;
  }
  return (
    <Typography
      style={{
        backgroundColor: color,
        border: "1px solid",
        borderColor: color,
        borderRadius: "12px",
        color: "#fff",
        fontSize: "0.875rem",
        marginTop: "-3px",
        padding: "3px 12px"
      }}
      variant={"body1"}
    >
      {text}
    </Typography>
  );
};

const setTextTwoCheckedCard = (card) => ({ type: "CHECK_CARD_STEP_TWO", payload: card });

export default function MobileStepTwo() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { width, height } = useWindowDimensions();

  const checkedCardStepOne = useSelector((state) => state.checkedCardStepOne);
  const checkedCardStepTwo = useSelector((state) => state.checkedCardStepTwo);

  const mappedTexts = React.useMemo(() => _.map(stepTwoTexts, (text) => ({ heading: text[0], text: text[1] })), []);
  const mobileSelectedTextCardHeading = React.useMemo(
    () => stepOneTexts[_.findIndex(stepOneTexts, (text) => text[1] === checkedCardStepOne)][0],
    [checkedCardStepOne]
  );

  const onTextCardClick = React.useCallback(
    (text) => {
      if (checkedCardStepTwo !== text) {
        dispatch(setTextTwoCheckedCard(text));
      }
    },
    [checkedCardStepTwo, dispatch]
  );

  return (
    <div className={classes.root}>
      {height > 440 && <MobileSelectedTextCard heading={mobileSelectedTextCardHeading} text={checkedCardStepOne} />}
      <Carousel
        autoPlay={false}
        animation={"slide"}
        className={classes.carousel}
        fullHeightHover={false}
        navButtonsAlwaysInvisible={true}
        indicatorIconButtonProps={{
          style: {
            color: "rgba(0, 0, 0, 0.54)"
          }
        }}
        activeIndicatorIconButtonProps={{
          style: {
            color: "#C51344"
          }
        }}
      >
        {mappedTexts.map((item, i) => (
          <Item key={i} item={item} onClick={onTextCardClick} />
        ))}
      </Carousel>
    </div>
  );
}

function Item(props) {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const { width, height } = useWindowDimensions();

  const checkedCardStepOne = useSelector((state) => state.checkedCardStepOne);
  const checkedCardStepTwo = useSelector((state) => state.checkedCardStepTwo);
  const sentenceScore = useSelector((state) => state.sentenceScore);

  const relation = React.useMemo(
    () => sentenceScore[checkedCardStepOne + " " + props.item.text],
    [checkedCardStepOne, sentenceScore, props.item.text]
  );

  const toggleDialog = React.useCallback(() => {
    setDialogOpen(!dialogOpen);
  }, [dialogOpen]);

  const isSelected = React.useMemo(
    () => checkedCardStepOne === props.item.text || checkedCardStepTwo === props.item.text,
    [checkedCardStepOne, checkedCardStepTwo, props.item.text]
  );

  const updateSelected = React.useCallback(() => {
    props.onClick(props.item.text);
  }, [props]);

  return (
    <div>
      <MobileDialog heading={props.item.heading} onClose={toggleDialog} open={dialogOpen} text={props.item.text} />
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <div className={classes.heading}>
            <Typography className={classes.cardHeading}>{props.item.heading}</Typography>
            {relation !== undefined ? mapRelationToText(relation) : null}
          </div>
          <div
            className={classes.textOverflow}
            style={height > 440 ? { height: height - 396.5 } : { height: height - 285.5 }}
          >
            <Typography className={classes.text} color={"textSecondary"} variant={"body2"}>
              {props.item.text}
            </Typography>
          </div>
          <div className={classes.cardFooter}>
            <Link onClick={toggleDialog} variant={"body2"}>
              Mehr Lesen
            </Link>
            {
              <Fab size="small" color="primary" aria-label="add" onClick={updateSelected}>
                {isSelected ? <Check /> : <Add />}
              </Fab>
            }
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
